import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  DialogActions,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, FormProvider } from "react-hook-form";
import ReplayIcon from "@mui/icons-material/Replay";
import { useDispatch } from "react-redux";
import styled from "@emotion/styled";
import { useDialog } from "../../../../contexts/DialogContext";
import CustomTextField from "../../../../components/CustomTextField";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../../redux/slices/snackbar";
import useTenant from "../../../../hooks/useTenant";
import usePassword from "../../../../hooks/usePassword";
import CachedIcon from "@mui/icons-material/Cached";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const validationSchema = Yup.object().shape({
  appPassword: Yup.string().max(255).required("App password is required"),
  confirmAppPassword: Yup.string()
    .required("App password is required")
    .when("appPassword", {
      is: (val) => (val && val.length > 0 ? true : false),
      then: () => Yup.string().oneOf([Yup.ref("appPassword")], "Both password need to be the same"),
    }),
});

const ChangeAppPassword = (props) => {
  const dispatch = useDispatch();
  const { isRenewSubmitting, updatePassword } = useTenant();
  const { userName } = props;

  const { getPassword, generatedPassword, hasError, errorMessage } = usePassword();

  const methods = useForm({
    resolver: yupResolver(validationSchema),
  });
  const {
    reset,
    handleSubmit,
    watch,
    resetField,
    formState: { errors },
    control,
    setValue,
  } = methods;

  const { unSetDialog } = useDialog();

  const { t } = useTranslation();

  const [showPassword, setShowPassword] = useState(false); // State to toggle password visibility

  const HoverIcon = styled(CachedIcon)(({ theme }) => ({
    transition: "transform 0.3s ease, color 0.3s ease",
    "&:hover": {
      transform: "rotate(90deg) scale(1.2)",
      color: theme.palette.primary.main,
    },
  }));

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev); // Toggle the password visibility
  };

  const generatePassword = async () => {
    const password = await getPassword();
    console.log("Generated Password:", password);
    setValue("appPassword", password.password);
    setValue("confirmAppPassword", password.password);
  };

  const onSubmit = async (data) => {
    try {
      const payload = {
        userName: userName,
        password: data.appPassword,
      };
      await updatePassword(payload);
      dispatch(showSuccessSnackbar("Password changed successfully!"));
      unSetDialog("change_password");
    } catch (ex) {
      dispatch(showErrorSnackbar(ex.message || ex.detail));
    }
  };

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={12} className="row" pt={8}>
            <Grid item md={6}>
              <CustomTextField
                type={showPassword ? "password" : "text"}
                label={t("password")}
                name="appPassword"
                my={2}
                control={control}
                fullWidth
                variant="outlined"
                errors={errors}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={generatePassword}>
                        <HoverIcon />
                      </IconButton>
                      <IconButton
                        onClick={togglePasswordVisibility}
                        edge="end"
                        aria-label="toggle password visibility"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item md={6}>
              <CustomTextField
                label={t("confirm_password")}
                name="confirmAppPassword"
                type="password"
                my={2}
                control={control}
                fullWidth
                variant="outlined"
                errors={errors}
              />
            </Grid>
          </Grid>
          <DialogActions>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<ReplayIcon />}
              endIcon={isRenewSubmitting ? <CircularProgress size={24} /> : null}
              disabled={false}
            >
              Save
            </Button>

            <Button variant="text" onClick={() => unSetDialog("change_password")} color="primary">
              Close
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </React.Fragment>
  );
};
export default ChangeAppPassword;
